import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import ClearanceUtils from '@src/slide/domain/ui-model/utils/clearance-utils';
import { observable } from 'mobx';
import { ISectionManager } from './section-manager.interface';

export default class SectionManager implements ISectionManager {
    @observable
    public sectionFoldedState: boolean[] | null = null;
    @observable
    public selectedSectionIndex = -1;
    @observable
    public nameEditingIndex = -1;
    @observable
    public onSectionsChangeAction = 0;

    private selectionService: ISelectionService;
    private uiDataService: IUiDataService;

    public setSelectionServiceDelegate(selectionService: ISelectionService): void {
        this.selectionService = selectionService;
    }

    public setUiDataServiceDelegate(uiDataService: IUiDataService): void {
        this.uiDataService = uiDataService;
    }

    public initSectionFoldedState(sectionLength: number): void {
        if (!this.sectionFoldedState || sectionLength !== this.sectionFoldedState.length) {
            this.sectionFoldedState = Array.from({ length: sectionLength }, () => false);
        }
    }

    public addUnfoldedSectionStateAt(clearanceIndex: number, addedSectionNum: number): void {
        const slideIndex = ClearanceUtils.calcSlideIndexFromClearanceIndex(
            clearanceIndex,
            this.uiDataService?.presentation.sectionList ?? [],
        );
        if (addedSectionNum === 2) {
            this.sectionFoldedState = [false, false];
            this.nameEditingIndex = 1;
            this.selectSection(1, slideIndex);
            return;
        }
        if (!this.sectionFoldedState) {
            return;
        }
        let sectionIndex = ClearanceUtils.calcSectionIndexFromClearanceIndex(
            clearanceIndex,
            this.uiDataService?.presentation.sectionList ?? [],
        ) + 1;
        if (clearanceIndex === 0 && this.uiDataService?.presentation.children.length !== 0) {
            sectionIndex = 0;
        }
        if (sectionIndex !== -1 && sectionIndex <= this.sectionFoldedState.length) {
            this.sectionFoldedState.splice(sectionIndex, 0, false);
            this.nameEditingIndex = sectionIndex;
            this.selectSection(sectionIndex, slideIndex);
        }
    }

    public deleteSectionStateAt(sectionIndex: number): void {
        this.sectionFoldedState?.splice(sectionIndex, 1);
        this.selectedSectionIndex = -1;
    }

    public deleteAllSectionsState(): void {
        this.sectionFoldedState = null;
    }

    public moveSectionFoldedState(from: number, to: number): void {
        if (!this.sectionFoldedState) {
            return;
        }
        if (from < 0 || from >= this.sectionFoldedState.length) {
            return;
        }
        if (to < 0 || to >= this.sectionFoldedState.length) {
            return;
        }
        const newSectionFoldedState = [...this.sectionFoldedState];
        const [fromFoldedState] = newSectionFoldedState.splice(from, 1);
        newSectionFoldedState.splice(to, 0, fromFoldedState);
        this.sectionFoldedState = newSectionFoldedState;
    }

    public selectSection(sectionIndex: number, slideBeginIndex: number, slideModels?: ISlideModel[]): void {
        this.selectedSectionIndex = sectionIndex;
        if (this.selectionService) {
            const currentSlideIndex = Math.min(this.uiDataService.presentation.children.length - 1, slideBeginIndex);
            this.selectionService.currentSlideIndex = currentSlideIndex;
        }
        if (!slideModels || slideModels.length === 0) {
            return;
        }
        this.selectionService?.selectedElements.clear();
        this.selectionService?.selectedSlides.clear();
        this.selectionService?.selectedSlides.add(...slideModels);
    }

    public collapseAllSections(): void {
        if (!this.sectionFoldedState) {
            return;
        }
        this.sectionFoldedState = this.sectionFoldedState.map(() => true);
    }

    public expandAllSections(): void {
        if (!this.sectionFoldedState) {
            return;
        }
        this.sectionFoldedState = this.sectionFoldedState.map(() => false);
    }

    public expandSelectedSection(): void {
        if (!this.sectionFoldedState || this.selectedSectionIndex < 0) {
            return;
        }
        const { sectionFoldedState: oldSectionFoldedState } = this;
        const sectionFoldedState = [...oldSectionFoldedState];
        sectionFoldedState[this.selectedSectionIndex] = false;
        this.sectionFoldedState = sectionFoldedState;
    }

    public expandCurrentSection(slideClearanceIndex?: number): void {
        let sectionIndex = ClearanceUtils.calcSectionIndexFromClearanceIndex(
            slideClearanceIndex ?? this.selectionService.slideClearanceIndex,
            this.uiDataService.presentation.sectionList ?? [],
        );
        if (sectionIndex === -1) {
            sectionIndex = ClearanceUtils.calcSectionIndexFromSlideIndex(
                this.selectionService.currentSlideIndex,
                this.uiDataService.presentation.sectionList ?? [],
            );
        }
        if (sectionIndex === -1) {
            return;
        }
        if (this.sectionFoldedState) {
            const sectionFoldedState = [...this.sectionFoldedState];
            sectionFoldedState[sectionIndex] = false;
            this.sectionFoldedState = sectionFoldedState;
        }
    }
}
